import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { UseCases } from "@planzone/core";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { Calendar } from "@common/calendar";
import { LayoutWatcher } from "@common/layoutWatcher";
import { ScreenWatcher } from "@common/screenWatcher";
import {
  useEditEvent,
  useListEdition,
  useEditTask,
  useMilestoneEdition,
  useRetrieveWbsMeta,
} from "@planzone/view-models";
import { usePermissions } from "@scenes/safeguard";
import {
  task as taskMock,
  list as listMock,
  OptimizedGantt,
  task,
  milestone,
  event,
} from "@planzone/dto";

import {
  isDone,
  isEvent,
  isList,
  isMilestone,
  isProject,
  isTask,
} from "@scenes/optimizedGantt/graph/elementsOverlay/element/utils";
import { reCalculateDateRange } from "@scenes/optimizedGantt/utils";

import { Props } from "./types";
import styles from "./styles.scss";

// Same shape as useElementEdition in graph/elementsOverlay/element/utils/edit.ts.
// Round Y/M/D/H/M in user TZ to UTC ISO, dropping seconds/ms. The two hooks
// are duplicates flagged in gantt-date-audit §12 Q8 — kept in lockstep until
// the de-dup follow-up.
function toEventTimeIso(iso: string, userTimezone: string): string {
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(userTimezone);
  return (
    DateTime.fromObject(
      {
        year: dt.year,
        month: dt.month,
        day: dt.day,
        hour: dt.hour,
        minute: dt.minute,
      },
      { zone: userTimezone },
    )
      .toUTC()
      .toISO() ?? ""
  );
}

function useEditElement(element: OptimizedGantt[number]) {
  const userTimezone = DateTime.local().zoneName;
  const { isRequestPending: isTaskEditionRequestPending, editTask } =
    useEditTask({ ...task, ...element });
  const { isRequestPending: isListEditionRequestPending, editList } =
    useListEdition();
  const { isRequestPending: isMilestoneEditionRequestPending, editMilestone } =
    useMilestoneEdition({ ...milestone, ...element });
  const { isRequestPending: isEventEditionRequestPending, editEvent } =
    useEditEvent({ ...event, ...element });

  return {
    isEditionRequestPending: isTask(element)
      ? isTaskEditionRequestPending
      : isList(element)
        ? isListEditionRequestPending
        : isMilestone(element)
          ? isMilestoneEditionRequestPending
          : isEvent(element)
            ? isEventEditionRequestPending
            : false,
    editElement: (nextElement: OptimizedGantt[number]) => {
      return isTask(element)
        ? !isTaskEditionRequestPending &&
            editTask({
              nextTask: {
                ...taskMock,
                ...element,
                activityKeyRank: nextElement.keyRank,
                startDate: nextElement.startDate,
                endDate: nextElement.endDate,
              },
              task: {
                ...taskMock,
                ...element,
                activityKeyRank: element.keyRank,
                // percentComplete: element.progress,
              },
            })
        : isMilestone(element)
          ? !isMilestoneEditionRequestPending &&
            editMilestone({
              nextMilestone: {
                ...milestone,
                ...element,
                keyRank: nextElement.keyRank,
                endDate: nextElement.endDate,
              },
              milestone: { ...milestone, ...element },
            })
          : isEvent(element)
            ? !isEventEditionRequestPending &&
              editEvent({
                nextEvent: {
                  ...event,
                  ...element,
                  startTime: toEventTimeIso(
                    nextElement.startDate,
                    userTimezone,
                  ),
                  endTime: toEventTimeIso(nextElement.endDate, userTimezone),
                },
                event: { ...event, ...element },
              })
            : isList(element)
              ? !isListEditionRequestPending &&
                editList({
                  list: { ...listMock, ...element },
                  nextList: {
                    ...listMock,
                    ...element,
                    startDate: nextElement.startDate,
                    endDate: nextElement.endDate,
                  },
                })
              : isProject(element) &&
                !isListEditionRequestPending &&
                editList({
                  list: {
                    ...listMock,
                    ...element,
                    uuid: element.activityRootUuid,
                  },
                  nextList: {
                    ...listMock,
                    ...element,
                    startDate: nextElement.startDate,
                    endDate: nextElement.endDate,
                  },
                });
    },
  };
}

function Dates({
  projects,
  element,
  minDate,
  maxDate,
  projectTimezone,
}: Props) {
  const { isEditionRequestPending, editElement } = useEditElement(element);
  const userTimezone = DateTime.local().zoneName;
  const min = DateTime.fromISO(minDate, { zone: "utc" });
  const max = DateTime.fromISO(maxDate, { zone: "utc" });
  const permissions = usePermissions([
    UseCases.editProject,
    UseCases.editList,
    UseCases.editTask,
    UseCases.editMilestone,
    UseCases.editEvent,
  ]);
  const canEdit = isProject(element)
    ? permissions.canEditProject
    : isList(element)
      ? permissions.canEditList
      : isTask(element)
        ? permissions.canEditTask
        : isMilestone(element)
          ? permissions.canEditMilestone
          : isEvent(element)
            ? permissions.canEditEvent
            : false;
  const recalculated = reCalculateDateRange(element, min, max);
  // reCalculateDateRange returns UTC-zoned DateTimes (preserved by design;
  // other consumers depend on its current output). Project to user TZ for
  // display so this surface matches every other date-display surface in the
  // app — same pattern as the calendar fix in 9050b5f39.
  const displayStart = recalculated.startDate?.setZone(userTimezone);
  const displayEnd = recalculated.endDate?.setZone(userTimezone);
  // Editing must stay anchored to the project's timezone (it's what
  // ActivityValidator on the backend compares day boundaries against) and
  // preserve the existing hour/minute, otherwise re-picking the displayed
  // day silently commits a different instant than what's stored.
  const editStart = recalculated.startDate?.setZone(projectTimezone);
  const editEnd = recalculated.endDate?.setZone(projectTimezone);
  // Bounds are hour-precise (e.g. an HOUR-granularity constraint), not
  // necessarily local midnight. The Calendar widget only lets the user pick
  // a day, so editDate must clamp to the bound's exact instant when the
  // picked day is the bound's day — otherwise the old date's hour/minute
  // gets reapplied to that day and can land before/after the real bound.
  const boundDateTime = (iso?: string | null) => {
    if (!iso) return undefined;
    const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(projectTimezone);
    return dt.isValid ? dt : undefined;
  };
  const startMinBound = isProject(element)
    ? undefined
    : boundDateTime(minDate || element.minStartDate);
  const startMaxBound = boundDateTime(
    isList(element) || isProject(element)
      ? element.maxStartDate || element.endDate || maxDate || element.maxEndDate
      : element.endDate || maxDate || element.maxEndDate,
  );
  const endMinBound = boundDateTime(
    isList(element) || isProject(element)
      ? element.minEndDate ||
          element.startDate ||
          minDate ||
          element.minStartDate
      : element.startDate || minDate || element.minStartDate,
  );
  const canOpenCalendar = !isDone(element) && canEdit;
  const { meta } = useRetrieveWbsMeta(projects);
  const elementMeta = meta[element.uuid] || {
    uuid: "",
    allocatedHours: 0,
    attachmentCount: 0,
    commentCount: 0,
    elementCount: 0,
    duration: 0,
    percentComplete: 0,
    spentHours: 0,
  };

  return (
    <div
      className={classNames(styles.details, {
        [styles.list]: isList(element),
        [styles.project]: isProject(element),
      })}
    >
      {isMilestone(element) ? (
        <div className={styles.block}>
          <p className={styles.text}>-</p>
        </div>
      ) : (
        <VisibilityWatcher>
          {(isVisible, show, hide) => (
            <ScreenWatcher
              renderChildren={({ height: screenHeight }) => (
                <LayoutWatcher
                  renderChildren={({ top, height }) => (
                    <div
                      className={classNames(styles.block, {
                        [styles.closedCalendar]: !canOpenCalendar,
                      })}
                    >
                      <p
                        className={classNames(styles.text, {
                          [styles.recalculated]:
                            !!displayStart && !element.startDate,
                        })}
                        onClick={() =>
                          canOpenCalendar && (isVisible ? hide() : show())
                        }
                      >
                        {displayStart
                          ? !!element.startDate
                            ? displayStart.toLocaleString(DateTime.DATE_SHORT)
                            : `(${displayStart.toLocaleString(DateTime.DATE_SHORT)})`
                          : "-"}
                      </p>
                      {isVisible && canOpenCalendar && (
                        <div
                          className={styles.calendar}
                          style={{
                            bottom:
                              top + height + 200 > screenHeight
                                ? `calc(100% + 1rem)`
                                : undefined,
                            top:
                              top + height + 200 > screenHeight
                                ? "initial"
                                : `calc(100% + 1rem)`,
                          }}
                        >
                          <Calendar
                            date={{
                              year: editStart.year,
                              month: editStart.month - 1,
                              day: editStart.day,
                            }}
                            minDate={
                              startMinBound
                                ? {
                                    year: startMinBound.year,
                                    month: startMinBound.month - 1,
                                    day: startMinBound.day,
                                  }
                                : undefined
                            }
                            maxDate={
                              startMaxBound
                                ? {
                                    year: startMaxBound.year,
                                    month: startMaxBound.month - 1,
                                    day: startMaxBound.day,
                                  }
                                : undefined
                            }
                            editDate={(date) => {
                              const picked = DateTime.fromObject(
                                {
                                  year: date.year,
                                  month: date.month + 1,
                                  day: date.day,
                                  hour: editStart.isValid ? editStart.hour : 0,
                                  minute: editStart.isValid
                                    ? editStart.minute
                                    : 0,
                                },
                                { zone: projectTimezone },
                              );
                              const clamped =
                                startMinBound && picked < startMinBound
                                  ? startMinBound
                                  : startMaxBound && picked > startMaxBound
                                    ? startMaxBound
                                    : picked;
                              !isEditionRequestPending &&
                                editElement({
                                  ...element,
                                  startDate: clamped.toUTC().toISO() ?? "",
                                });
                              hide();
                            }}
                            clear={
                              isProject(element)
                                ? undefined
                                : () => {
                                    !isEditionRequestPending &&
                                      editElement({
                                        ...element,
                                        startDate: "",
                                      });
                                    hide();
                                  }
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                />
              )}
            />
          )}
        </VisibilityWatcher>
      )}
      <VisibilityWatcher>
        {(isVisible, show, hide) => (
          <ScreenWatcher
            renderChildren={({ height: screenHeight }) => (
              <LayoutWatcher
                renderChildren={({ top, height }) => (
                  <div
                    className={classNames(styles.block, {
                      [styles.closedCalendar]: !canOpenCalendar,
                    })}
                  >
                    <p
                      className={classNames(styles.text, {
                        [styles.recalculated]:
                          !!displayStart && !element.endDate,
                      })}
                      onClick={() =>
                        canOpenCalendar && (isVisible ? hide() : show())
                      }
                    >
                      {displayEnd
                        ? !!element.endDate
                          ? displayEnd.toLocaleString(DateTime.DATE_SHORT)
                          : `(${displayEnd.toLocaleString(DateTime.DATE_SHORT)})`
                        : "-"}
                    </p>
                    {isVisible && canOpenCalendar && (
                      <div
                        className={styles.calendar}
                        style={{
                          bottom:
                            top + height + 200 > screenHeight
                              ? `calc(100% + 1rem)`
                              : undefined,
                          top:
                            top + height + 200 > screenHeight
                              ? "initial"
                              : `calc(100% + 1rem)`,
                        }}
                      >
                        <Calendar
                          date={{
                            year: editEnd.year,
                            month: editEnd.month - 1,
                            day: editEnd.day,
                          }}
                          minDate={
                            endMinBound
                              ? {
                                  year: endMinBound.year,
                                  month: endMinBound.month - 1,
                                  day: endMinBound.day,
                                }
                              : undefined
                          }
                          editDate={(date) => {
                            const picked = DateTime.fromObject(
                              {
                                year: date.year,
                                month: date.month + 1,
                                day: date.day,
                                hour: editEnd.isValid ? editEnd.hour : 0,
                                minute: editEnd.isValid ? editEnd.minute : 0,
                              },
                              { zone: projectTimezone },
                            );
                            const clamped =
                              endMinBound && picked < endMinBound
                                ? endMinBound
                                : picked;
                            !isEditionRequestPending &&
                              editElement({
                                ...element,
                                endDate: clamped.toUTC().toISO() ?? "",
                              });
                            hide();
                          }}
                          clear={
                            isProject(element)
                              ? undefined
                              : () => {
                                  !isEditionRequestPending &&
                                    editElement({
                                      ...element,
                                      endDate: "",
                                    });
                                  hide();
                                }
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              />
            )}
          />
        )}
      </VisibilityWatcher>
      <div
        className={classNames(styles.block, styles.readOnly, {
          [styles.closedCalendar]: !canOpenCalendar,
        })}
      >
        <p className={styles.text}>
          {elementMeta.duration ? elementMeta.duration : "-"}
        </p>
      </div>
    </div>
  );
}

export { Dates };
